/**
 * Skirmish setup — faction select, reference-styled (theme.css).
 * Cards for each playable faction show a strengths summary (derived from
 * the faction's canonical military/economy one-liners) and its signature
 * units, each with the unit's animated medallion (medallions play their
 * intro once when the cards first appear). An AI difficulty picker, a
 * map-size picker, and the camera-drag toggle sit above the cards.
 *
 * Strengths are derived from canonical data fields (identity, military,
 * economy) — no invented text. Long-term the data should grow explicit
 * `strengths` / `signature_units` fields.
 * Signature-unit picks below are prototype-side UI curation over real
 * canonical unit ids (never renames, never data edits).
 *
 * Camera invert is read/written through the shared `settings` store so the
 * toggle here and the one in Settings can never disagree.
 */
import type { FactionDef } from "../data/types";
import type { GameDB } from "../data/gamedb";
import type { MedallionSpec } from "../render/medallions";
import { artKeyFor } from "../data/artmap";
import { DIFFICULTIES, DIFFICULTY_IDS, type DifficultyId } from "../sim/difficulty";
import {
  MAP_SIZES,
  MAP_SIZE_ORDER,
  defaultMapSizeId,
  isMapSizeId,
  type MapSizeId,
} from "../sim/mapconfig";
import { settings } from "./settings";

export interface FactionChoice {
  factionId: string;
  difficulty: DifficultyId;
  mapSize: MapSizeId;
  cameraInvert: boolean;
}

export interface MedallionLike {
  attachWithIntro(canvas: HTMLCanvasElement | null, spec: MedallionSpec): void;
  attachStatic(canvas: HTMLCanvasElement | null, spec: MedallionSpec): void;
}

export interface FactionSelectCtx {
  db: GameDB;
  medallions: MedallionLike;
  /** Called when the player backs out to the main menu (optional). */
  onBack?: () => void;
}

const DIFFICULTY_BLURBS: Record<DifficultyId, string> = {
  easy: "A gentle opponent: slower economy, small raids, never assaults your command building.",
  advanced: "A credible opponent: steady economy, scouts, and mixed raids and assaults.",
  expert: "Relentless: faster economy and training, larger waves with cavalry, early assaults.",
};

/** Persisted skirmish setup (difficulty + map size). Camera invert lives
 *  in the shared settings store. */
const SETUP_KEY = "legionnaire-skirmish-setup";
interface SavedSetup {
  difficulty?: DifficultyId;
  mapSize?: MapSizeId;
}
function loadSetup(): SavedSetup {
  try {
    const raw = localStorage.getItem(SETUP_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as SavedSetup;
    return {
      difficulty:
        v.difficulty === "easy" ||
        v.difficulty === "advanced" ||
        v.difficulty === "expert"
          ? v.difficulty
          : undefined,
      mapSize: isMapSizeId(v.mapSize) ? v.mapSize : undefined,
    };
  } catch {
    return {};
  }
}
function saveSetup(s: SavedSetup): void {
  try {
    localStorage.setItem(SETUP_KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable — setup just won't persist */
  }
}

/** Canonical unit ids, per playable faction. Filtered against db.units at render time. */
const SIGNATURE_UNITS: Record<string, string[]> = {
  rome: ["praetorian", "triarii", "aquilifer"],
  samnite: ["samnite_linen_legion", "samnite_oath_guard", "samnite_warrior"],
  gauls: ["gaul_gaesatae", "gaul_noble_cavalry", "gaul_chariot"],
};

/** Canonical signature-unit ids curated per faction; callers filter against db.units. */
export function signatureUnitIds(factionId: string): string[] {
  return SIGNATURE_UNITS[factionId] ?? [];
}

/** Read a free-text canonical field (identity / military / economy). */
function strField(f: FactionDef, key: string): string {
  const v = f[key];
  return typeof v === "string" ? v : "";
}

function hexToNumber(css: string | undefined): number {
  const m =
    typeof css === "string" ? /^#([0-9a-f]{6})$/i.exec(css.trim()) : null;
  return m ? parseInt(m[1], 16) : 0xb98b3a;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Show the skirmish setup screen. Resolves with the player's choice, or
 * `null` when the player backs out to the main menu.
 */
export function chooseFaction(
  factions: FactionDef[],
  ctx: FactionSelectCtx,
): Promise<FactionChoice | null> {
  return new Promise((resolve) => {
    const saved = loadSetup();
    const overlay = document.createElement("div");
    overlay.className = "lg-screen";
    overlay.id = "lg-setup";
    overlay.innerHTML =
      `<div class="lg-sky"></div><div class="lg-scrim"></div><div class="lg-vig"></div>` +
      `<div class="lg-body">` +
      `<div class="lg-crumb lg-anim">Skirmish</div>` +
      `<div class="lg-title lg-anim lg-d1" style="font-size:clamp(26px,5.4vw,40px)">CHOOSE YOUR FACTION</div>` +
      `<div class="lg-sub lg-anim lg-d1">Destroy the enemy command building.</div>` +
      `<div class="lg-card lg-anim lg-d2" style="margin-top:18px">` +
      `<div class="lg-hd" style="margin-top:0">Enemy AI difficulty</div>` +
      `<div class="lg-seg" id="lg-diff-seg" role="group" aria-label="AI difficulty"></div>` +
      `<div class="lg-note" id="lg-diff-blurb" style="margin:10px 0 4px"></div>` +
      `<div class="lg-hd">Map size</div>` +
      `<div class="lg-seg" id="lg-map-seg" role="group" aria-label="Map size"></div>` +
      `<div class="lg-note" id="lg-map-blurb" style="margin:10px 0 4px"></div>` +
      `<div class="lg-hd">Camera</div>` +
      `<button class="lg-toggle" id="lg-cam-toggle" aria-pressed="false" aria-label="Toggle inverted camera drag">` +
      `<span>Camera drag</span><span class="tk"></span></button>` +
      `<div class="lg-note" id="lg-cam-desc" style="margin:8px 0 0"></div>` +
      `</div>` +
      `<div class="lg-hd lg-anim lg-d2" style="max-width:1060px">Factions</div>` +
      `<div class="lg-factions lg-anim lg-d2" id="lg-faction-row"></div>` +
      `<div class="lg-label lg-anim lg-d3" id="lg-lock-label" style="margin-top:26px;display:none">More factions — not yet playable</div>` +
      `<div class="lg-chips lg-anim lg-d3" id="lg-lock-row" style="display:none"></div>` +
      `<div class="lg-btn-row lg-anim lg-d3" style="margin-top:26px;max-width:640px">` +
      `<button class="lg-btn" id="lg-setup-back">Back</button>` +
      `</div>` +
      `</div>`;

    const finish = (choice: FactionChoice | null): void => {
      unsubSettings();
      overlay.remove();
      resolve(choice);
    };

    // --- Difficulty ---
    let difficulty: DifficultyId = saved.difficulty ?? "advanced";
    const persist = (): void => saveSetup({ difficulty, mapSize });
    const diffSeg = overlay.querySelector("#lg-diff-seg") as HTMLDivElement;
    const diffBlurb = overlay.querySelector("#lg-diff-blurb") as HTMLDivElement;
    const refreshDiff = (): void => {
      diffSeg.querySelectorAll("button").forEach((b) => {
        b.classList.toggle("on", b.dataset.id === difficulty);
      });
      diffBlurb.textContent = DIFFICULTY_BLURBS[difficulty];
    };
    for (const id of DIFFICULTY_IDS) {
      const btn = document.createElement("button");
      btn.dataset.id = id;
      btn.textContent = DIFFICULTIES[id].label;
      btn.addEventListener("click", () => {
        difficulty = id;
        refreshDiff();
        persist();
      });
      diffSeg.appendChild(btn);
    }

    // --- Map size: Small default on touch/narrow, Standard on desktop ---
    let mapSize: MapSizeId =
      saved.mapSize ??
      defaultMapSizeId({
        coarsePointer:
          typeof window.matchMedia === "function" &&
          window.matchMedia("(pointer: coarse)").matches,
        narrowScreen: window.innerWidth < 720,
      });
    const mapSeg = overlay.querySelector("#lg-map-seg") as HTMLDivElement;
    const mapBlurb = overlay.querySelector("#lg-map-blurb") as HTMLDivElement;
    const refreshMap = (): void => {
      mapSeg.querySelectorAll("button").forEach((b) => {
        b.classList.toggle("on", b.dataset.id === mapSize);
      });
      const def = MAP_SIZES[mapSize];
      mapBlurb.textContent = `${def.name} — ${def.la}. ${def.blurb}`;
    };
    for (const id of MAP_SIZE_ORDER) {
      const def = MAP_SIZES[id];
      const btn = document.createElement("button");
      btn.dataset.id = id;
      btn.textContent = def.name;
      btn.title = `${def.la} — ${def.blurb}`;
      btn.setAttribute("aria-label", `Map size ${def.name}`);
      btn.addEventListener("click", () => {
        mapSize = id;
        refreshMap();
        persist();
      });
      mapSeg.appendChild(btn);
    }

    // --- Camera drag toggle (shared settings store) ---
    const camToggle = overlay.querySelector("#lg-cam-toggle") as HTMLButtonElement;
    const camDesc = overlay.querySelector("#lg-cam-desc") as HTMLDivElement;
    const refreshCam = (): void => {
      const inv = settings.get().cameraInvert;
      camToggle.setAttribute("aria-pressed", inv ? "true" : "false");
      camToggle.querySelector("span")!.textContent = inv
        ? "Camera drag — inverted"
        : "Camera drag — world follows finger";
      camDesc.textContent = inv
        ? "Inverted — drag moves the camera with your finger (legacy RTS feel)."
        : "Standard — the world follows your finger (Google Maps feel).";
    };
    camToggle.addEventListener("click", () => {
      settings.update({ cameraInvert: !settings.get().cameraInvert });
    });
    const unsubSettings = settings.onChange(refreshCam);

    refreshDiff();
    refreshMap();
    refreshCam();

    // --- Faction cards ---
    const row = overlay.querySelector("#lg-faction-row") as HTMLDivElement;
    const playable = factions.filter((x) => x.playable && x.implemented !== false);
    for (const f of playable) {
      const palette = f.palette ?? {};
      const tint = hexToNumber(palette.primary);
      const identity = strField(f, "identity");
      const military = strField(f, "military");
      const economy = strField(f, "economy");
      const startUnits = Object.entries(f.starting_units ?? {})
        .map(([id, n]) => `${n}× ${id.replace(/_/g, " ")}`)
        .join(", ");

      const card = document.createElement("button");
      card.className = "lg-faction";
      card.setAttribute(
        "aria-label",
        `Play as ${f.name_en}`,
      );
      const sigCells: string[] = [];
      const sigIds = signatureUnitIds(f.id).filter((uid) => ctx.db.units.get(uid));
      card.innerHTML =
        `<div style="width:100%;height:6px;margin-bottom:12px;` +
        `background:${escapeHtml(palette.primary ?? "#888")};"></div>` +
        `<h3>${escapeHtml(f.name_en)}</h3>` +
        (f.name_la
          ? `<div class="la">${escapeHtml(f.name_la)}</div>`
          : "") +
        (identity ? `<p>${escapeHtml(identity)}</p>` : "") +
        (military
          ? `<p><b style="color:var(--bronze)">Military — </b>${escapeHtml(military)}</p>`
          : "") +
        (economy
          ? `<p><b style="color:var(--bronze)">Economy — </b>${escapeHtml(economy)}</p>`
          : "") +
        `<div class="lg-label" style="text-align:left;margin:10px 0 6px">Signature units</div>` +
        `<div class="sig" data-sig></div>` +
        `<div class="lg-note" style="text-align:left;margin-top:10px;font-size:13px">Starts with: ${escapeHtml(startUnits || "—")}</div>`;
      void sigCells;

      const sigRow = card.querySelector<HTMLElement>("[data-sig]")!;
      for (const uid of sigIds) {
        const unit = ctx.db.units.get(uid)!;
        const spec: MedallionSpec = {
          artKey: artKeyFor(ctx.db.unitModel(unit.id), unit.id),
          name: unit.name_en,
          tint,
          model: ctx.db.unitModel(unit.id),
        };
        const cell = document.createElement("div");
        cell.style.cssText =
          "display:flex;flex-direction:column;align-items:center;gap:4px;width:80px;";
        const canvas = document.createElement("canvas");
        canvas.width = 112;
        canvas.height = 112;
        const label = document.createElement("div");
        label.style.cssText =
          "font-size:10px;color:#c9c2b2;text-align:center;line-height:1.2;";
        label.textContent = unit.name_en;
        cell.append(canvas, label);
        sigRow.appendChild(cell);
        try {
          ctx.medallions.attachWithIntro(canvas, spec);
        } catch {
          ctx.medallions.attachStatic(canvas, spec);
        }
      }

      card.addEventListener("click", () => {
        finish({
          factionId: f.id,
          difficulty,
          mapSize,
          cameraInvert: settings.get().cameraInvert,
        });
      });
      row.appendChild(card);
    }

    // --- Locked factions ---
    const rest = factions.filter((x) => !(x.playable && x.implemented !== false));
    if (rest.length > 0) {
      (overlay.querySelector("#lg-lock-label") as HTMLDivElement).style.display = "";
      const lockRow = overlay.querySelector("#lg-lock-row") as HTMLDivElement;
      lockRow.style.display = "";
      for (const f of rest) {
        const chip = document.createElement("div");
        chip.className = "lg-chip";
        chip.textContent = f.name_en;
        chip.title = "Not yet playable";
        chip.style.opacity = "0.6";
        lockRow.appendChild(chip);
      }
    }

    // --- Back ---
    overlay
      .querySelector("#lg-setup-back")!
      .addEventListener("click", () => {
        ctx.onBack?.();
        finish(null);
      });

    document.body.appendChild(overlay);
  });
}
